import { BreadCrumbs, Filter, HomeIcon, Input, MultiSelect } from '@isdd/idsk-ui-kit/index'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RELATION_TYPE } from '@isdd/metais-common/api'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { JOIN_OPERATOR, OPERATOR_SEPARATOR, RELATION_FILTER_PREFIX } from '@isdd/metais-common/constants'
import { DynamicFilterAttributes, ExtendedAttribute } from '@isdd/metais-common/components/dynamicFilterAttributes/DynamicFilterAttributes'
import { MetainformationColumns } from '@isdd/metais-common/componentHelpers/ci/getCiDefaultMetaAttributes'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { AttributeProfile } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { useSearchParams } from 'react-router-dom'
import { RowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { Languages } from '@isdd/metais-common/localization/languages'
import { join } from 'lodash'

import { CIFilterData } from '@/pages/ci/[entityName]/entity'
import { getCiHowToBreadCrumb, useGetEntityParamsFromUrl } from '@/componentHelpers/ci'
import { MainContentWrapper } from '@/components/MainContentWrapper'
import { ListWrapper } from '@/components/list-wrapper/ListWrapper'
import { CiListWizard } from '@/components/wizards/CiListWizard'
import { CiListContainer } from '@/components/containers/CiListContainer'

const GestorKSRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_gestor_KS}` as 'GestorKSRelType'

const ProjektKSRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Projekt_realizuje_KS}` as 'ProjektKSRelType'

export interface KSFilterData extends CIFilterData {
    [GestorKSRelType]?: string
    [ProjektKSRelType]?: string
    'EA_Profil_KS_typ_ks--eq'?: string
}
export interface KSFilterProps {
    defaultFilterValues: KSFilterData
    entityName: string
    codePrefix: string
    constraintsData: (EnumType | undefined)[] | undefined
    attributes: ExtendedAttribute[] | undefined
    attributeProfiles: AttributeProfile[] | undefined
    rowSelection: RowSelection<object>
}

export const KSFilter: React.FC<KSFilterProps> = ({
    defaultFilterValues,
    attributes,
    attributeProfiles,
    constraintsData,
    codePrefix,
    entityName,
    rowSelection,
}) => {
    const { t, i18n } = useTranslation()
    const [urlParams] = useSearchParams()
    const gestorRelationFromUrl = urlParams.get(GestorKSRelType)
    const ProjektRelationFromUrl = urlParams.get(ProjektKSRelType)
    const KSTypeFromUrl = urlParams.get('EA_Profil_KS_typ_ks--eq')
    const [KSType, setKSType] = useState(
        KSTypeFromUrl
            ? KSTypeFromUrl.includes(JOIN_OPERATOR)
                ? KSTypeFromUrl.split(JOIN_OPERATOR)
                : [KSTypeFromUrl]
            : [defaultFilterValues['EA_Profil_KS_typ_ks--eq'] as string],
    )

    const typKSConstraints = constraintsData?.find((c) => c?.code === 'TYP_KS')
    const optionsForSelects =
        typKSConstraints?.enumItems
            ?.filter((item) => item.valid)
            .map((item) => {
                const qualifier =
                    i18n.language === Languages.SLOVAK
                        ? item.qualifierName
                            ? `(${item.qualifierName})`
                            : ''
                        : item.qualifierNameEng
                        ? `(${item.qualifierNameEng})`
                        : ''
                return {
                    label: item.description ? `${item.description} ${qualifier}` : `${item.value} ${qualifier}`,
                    value: `${item.code}`,
                }
            }) ?? []

    return (
        <Filter<KSFilterData, object>
            rowSelection={rowSelection}
            defaultFilterValues={defaultFilterValues}
            form={({ register, filter, setValue, isOpen }) => {
                return (
                    <>
                        <CiLazySelect
                            ciType={'PO'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_gestor_KS}`)}
                            name={GestorKSRelType}
                            setValue={setValue}
                            defaultValue={
                                gestorRelationFromUrl
                                    ? gestorRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? gestorRelationFromUrl.split(JOIN_OPERATOR)
                                        : gestorRelationFromUrl
                                    : defaultFilterValues[GestorKSRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={'Projekt'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Projekt_realizuje_KS}`)}
                            name={ProjektKSRelType}
                            setValue={setValue}
                            defaultValue={
                                ProjektRelationFromUrl
                                    ? ProjektRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? ProjektRelationFromUrl.split(JOIN_OPERATOR)
                                        : ProjektRelationFromUrl
                                    : defaultFilterValues[ProjektKSRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />

                        <Input
                            id="name"
                            label={t(`filter.${entityName}.name`)}
                            placeholder={t(`filter.namePlaceholder`)}
                            {...register('Gen_Profil_nazov')}
                        />
                        <Input
                            id="metais-code"
                            label={t('filter.metaisCode.label')}
                            placeholder={codePrefix}
                            {...register('Gen_Profil_kod_metais')}
                        />
                        <MultiSelect
                            options={optionsForSelects}
                            setValue={setValue}
                            id="typ_sluzby"
                            label={typKSConstraints?.name}
                            {...register('EA_Profil_KS_typ_ks--eq')}
                            placeholder={t('customAttributeFilter.value.placeholderSelectMulti')}
                            onChange={(newValue) => {
                                setValue('EA_Profil_KS_typ_ks--eq', Array.isArray(newValue) ? join(newValue, '+') : newValue)
                                setKSType(newValue)
                            }}
                            value={KSType}
                        />
                        <DynamicFilterAttributes
                            ciName={entityName}
                            setValue={setValue}
                            defaults={defaultFilterValues}
                            filterData={{
                                attributeFilters: filter.attributeFilters ?? {},
                                metaAttributeFilters: filter.metaAttributeFilters ?? {},
                            }}
                            attributes={attributes as ExtendedAttribute[]}
                            attributeProfiles={attributeProfiles}
                            constraintsData={constraintsData}
                            isFocusable={isOpen}
                            ignoreInputNames={[MetainformationColumns.OWNER, 'EA_Profil_KS_typ_ks']}
                        />
                    </>
                )
            }}
        />
    )
}

export const KSListPage: React.FC = () => {
    const { entityName: ciType } = useGetEntityParamsFromUrl()
    const { t } = useTranslation()

    const defaultFilterValues: KSFilterData = {
        Gen_Profil_nazov: '',
        Gen_Profil_kod_metais: '',
        [GestorKSRelType]: '',
        [ProjektKSRelType]: '',
        'EA_Profil_KS_typ_ks--eq': '',
    }

    return (
        <>
            <CiListContainer<CIFilterData>
                entityName={ciType ?? ''}
                defaultFilterValues={defaultFilterValues}
                ListComponent={(props) => (
                    <>
                        <BreadCrumbs
                            withWidthContainer
                            links={[
                                { label: t('breadcrumbs.home'), href: '/', icon: HomeIcon },
                                ...getCiHowToBreadCrumb(props.entityName, t),
                                {
                                    label: t('titles.ciList', { ci: props.ciTypeData?.name }) ?? '',
                                    href: `/ci/${ciType}`,
                                },
                            ]}
                        />
                        {!props.isLoading && <CiListWizard />}
                        <MainContentWrapper>
                            <ListWrapper {...props} customFilter={(filterProps) => <KSFilter {...filterProps} />} />
                        </MainContentWrapper>
                    </>
                )}
            />
        </>
    )
}
